import { Box, Typography } from "@mui/material";
import { FaDownload } from "react-icons/fa";
import { useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";

import { colors } from "../../styles/colors";
import {
  acceptStyle,
  BrowsButtonUI,
  dragAndDropStyle,
  FileText,
  ReferralDocMain,
  rejectStyle,
} from "./styles/style";
import { flexAllCentre } from "../../styles/mui/styles/display";
import fontWeight from "../../styles/mui/fontWeight";

interface FileData {
  disabled?: boolean;
  fileName?: any;
  files: any;
  imageUrl?: string;
  patientImageProps?: any;
  setFiles: any;
  setPatientImage?: any;
  maxFileSizeInBytes?: number;
  minFileSize?: number;
  type: number;
}

type AllProps = FileData;

const FileUpload: React.FC<AllProps> = ({
  files,
  setFiles,
  maxFileSizeInBytes,
  minFileSize,
  type,
}: AllProps) => {
  const allowedFileTypes = [".doc", ".docx", ".pdf", ".xls", ".xlsx", ".txt"];
  const imageFileTypes = [".png", ".jpg", ".jpeg", ".jif", "jpe", ".pjp", ".pjpeg"];
  const portableFileTypes = [".pdf"];
  const faxFiletypes = {
    "image/png": imageFileTypes,
    "image/jpeg": imageFileTypes,
    "image/jpg": imageFileTypes,
    "application/pdf": portableFileTypes,
    "text/plain": [".txt"],
  };

  const portableFiletypes = {
    "application/pdf": portableFileTypes,
  };

  const acceptedFileType = (type: number) => {
    switch (type) {
      case 0:
        return faxFiletypes;
      case 2:
        return portableFiletypes;
      default:
        return {
          ...faxFiletypes,
          "application/vnd.ms-excel": allowedFileTypes,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            allowedFileTypes,
          "application/msword": allowedFileTypes,
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            allowedFileTypes,
        };
    }
  };

  const {
    getRootProps: getRootReferralFileProps,
    getInputProps: getReferralFileProps,
    isDragAccept,
    isDragReject,
    open,
    fileRejections,
  } = useDropzone({
    onDrop: useCallback(
      (acceptedFiles: any) => {
        if (acceptedFiles.length > 0) {
          const removeDuplicate = files.some(
            (file: { path: any }) => file.path === acceptedFiles[0].path
          );
          if (removeDuplicate === false) {
            type === 0
              ? setFiles([...files, acceptedFiles[0]])
              : setFiles([acceptedFiles[0]]);
          }
        }
      },
      [files]
    ),
    accept: {
      ...acceptedFileType(type),
    },
    maxFiles: 1,
    maxSize: maxFileSizeInBytes,
    minSize: minFileSize,
    noClick: true,
    noKeyboard: true,
    multiple: false,
  });

  const fileRejectionItems = fileRejections.map(({ errors }) => {
    const minFileSizeForUpload = minFileSize ? minFileSize : 1;
    const maxFileSizeInMegaBytes = maxFileSizeInBytes
      ? maxFileSizeInBytes / 1000000
      : 1000000;
    return (
      <Box key={maxFileSizeInMegaBytes} textAlign={"center"} mt="1rem">
        {errors.map((e: any) => {
          if (e.code === "file-invalid-type") {
            const allAllowedExtensions = [
              ...allowedFileTypes,
              ...imageFileTypes,
              ...portableFileTypes,
            ];
            const fileName = e.file?.name || "";
            const fileExt = fileName.slice(fileName.lastIndexOf(".")).toLowerCase();
            if (!allAllowedExtensions.includes(fileExt)) {
              e.message = `File is not allowed. Allowed types: ${allAllowedExtensions.join(", ")}`;
            } else {
              e.message = "File type not allowed";
            }
          } else if (e.code === "file-too-large") {
            e.message = `File is larger than ${maxFileSizeInMegaBytes} mb`;
          } else if (e.code === "file-too-small") {
            e.message = `File size should be greater than ${
              minFileSizeForUpload - 1
            } kb`;
          }
          return (
            <Typography
              variant="body1"
              color={colors.red[100]}
              key={e.code}
              fontWeight={fontWeight.Weight[3]}
              pb={"0.7rem"}
            >
              {e.message}
            </Typography>
          );
        })}
      </Box>
    );
  });

  const style = useMemo(
    () => ({
      ...dragAndDropStyle,
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragAccept, isDragReject]
  );

  return (
    <Box>
      <Grid container sx={[flexAllCentre, ReferralDocMain]}>
        <Grid
          size={{ xs: 5 }}
          {...getRootReferralFileProps({
            style,
          })}
        >
          <Box>
            <input className="input-zone" {...getReferralFileProps()} />
            <Box>
              <Box sx={flexAllCentre} pt={"1rem"}>
                <FaDownload color={colors.fonts[18]} size={"1rem"} />
                <Typography
                  variant="body1"
                  color={colors.fonts[4]}
                  fontWeight={fontWeight.Weight[5]}
                  pl={"1rem"}
                >
                  Drop file here
                </Typography>
              </Box>
              <Box sx={FileText}></Box>
            </Box>
          </Box>
        </Grid>
        <Grid size={{ xs: 2 }} sx={flexAllCentre}>
          <Typography
            variant="body1"
            color={colors.fonts[4]}
            fontWeight={fontWeight.Weight[4]}
          >
            OR
          </Typography>
        </Grid>
        <Grid size={{ xs: 5 }}>
          <Grid container sx={flexAllCentre}>
            <Grid p="1rem" size={{ xs: 12 }} sx={flexAllCentre}>
              <Button variant="outlined" onClick={open} sx={BrowsButtonUI}>
                Browse
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {fileRejectionItems}
    </Box>
  );
};

export default FileUpload;
